import { useSearchParams } from 'next/navigation';
import '@nf/utils-common/fpw/fpw.extensions';
import { Maybe, Extractor, matchSome } from '@nf/utils-common/fpw/monadic';
import { Nullish } from '@nf/utils-common/fpw/monoid';
import { split, stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { convertNextSearchParamsToLowerCase } from '@nf/utils-common/url';
import { convertClientLocale, convertApiLocale } from '@nf/utils-common/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import type { DeepLinkSiteInfo } from '@nf/types/site';
import { useLocale } from 'next-intl';
import { SwitchFromEnum } from '@nf/types/site';
import { useGetSiteConfigs } from './query/use-site-configs';
import { LicenseeTypeEnums } from '@nf/types/site';
import { siteService } from '@/services/site.service';
import { useMemo } from 'react';

type MobileDeeplinkCompound = [
	sport: number | null,
	menuType: number | null,
	market: string | null,
];

export enum TokenTypeEnum {
	Default = -1,
	Neuron = 0,
	SwitchModeWithId = 1,
	SwitchModeWithJwt = 2,
	None = 3
}

interface GetMarketType {
	Success: boolean;
	Code: string | null;
	Message: string | null;
	Data: string;
}

export interface GetJwtToken {
	Success: boolean;
	Code: string | null;
	Message: string | null;
	Data: {
		Token: string;
		UserName: string;
		SiteName: string;
		Language?: string | undefined;
		Url: string;
		Skin?: number;
		DetectorDomains?: Array<string>;
		CustId?: number
	};
}

export type DeepTokenData = GetJwtToken['Data'] | null;

export interface RequestJwtTokenData {
	apiDomain: string;
	token: string;
	tokenType: TokenTypeEnum;
	language: string;
	deepLinkSiteInfo: DeepLinkSiteInfo;
	accessToken: string | undefined;
	isBefore: boolean;
	isExtend?: boolean;
	GalaxyUserServerGroup?: string;
	doUseRefreshToken: string | boolean;
	OriginalRefer?: string;
}

interface RequestDeeplinkSportData {
	apiDomain: string | undefined;
	market: string;
	leagueId: number;
	matchId: number;
	authToken: string | undefined;
}

export type GalaxyDeepLinkSiteInfo = {
	homeUrl: string;
	depositUrl: string;
	extendSessionUrl: string;
	loginUrl: string;
	signUpUrl: string;
	game: string;
	gameId: string;
	oddsType: number; 
	skinColor: string;
	skinMode: number; 
	forceDarkMode: boolean | null;
	tsId: string;
	switchFrom: number; 
	isApp: boolean;
	themePreview: string;
	templateId: string;
	dptoken: string;
	switchFromUrl: string;
	lgId: string;
	types: string;
	childType: string;
	childTab: string;
	clearCount: number;
	targetMenuTab: number;
	goToType: string;
	// 新增投注偏好相關欄位
	bettingMode: number;
	betslipStyle: number;
};

export enum MenuTabEnum {
	Sports = 0,
	Home = 1,
}

export const GoToTypeMap = new Map<string, string>([
	['contactus', '/footer/contact-info'],
	['transfer', '/transaction/transfer'],
	['transaction', 'transaction/deposit'],
]);

/* support 3 method to use token to login */
const getTokenLoginUrl = (requestData: RequestJwtTokenData) => {
	const path = siteService.getLicenseeType() === LicenseeTypeEnums.Api ? 'ApiSiteLogin' : 'AthenaLogin';
	let url = `/api/${path}/ReGenerateToken`;
	if (requestData.token !== '') {
		switch (requestData.tokenType) {
			case TokenTypeEnum.Neuron:
				url = `/api/${path}/Login`;
				break;
			case TokenTypeEnum.SwitchModeWithId:
				url = `/api/${path}/OtherSkinModeLoginWithUser`;
				break;
			case TokenTypeEnum.SwitchModeWithJwt:
				url = `/api/${path}/OtherSkinModeLogin`;
				break;
		}
	}
	return new URL(url, requestData.apiDomain);
};

const getUseTokenLoginParameter = (requestData: RequestJwtTokenData) => {
	//ReGenerateToken
	const parseRequestData = {
		isBefore: requestData.isBefore,
		isExtend: requestData.isExtend,
		Token: requestData.token,
		Lang: convertApiLocale(requestData.language),
		GalaxyUserServerGroup: requestData.GalaxyUserServerGroup,
		OriginalRefer: requestData.OriginalRefer
	};
	if (requestData.deepLinkSiteInfo) {
		const skin = requestData.deepLinkSiteInfo.skinMode;
		const otype = requestData.deepLinkSiteInfo.oddsType;
		Object.assign(parseRequestData, {
			OType: otype.toString() == '-1' ? '' : otype.toString(),
			Skin: skin.toString() == '-1' ? '' : skin.toString(),
			Deposit: requestData.deepLinkSiteInfo.depositUrl,
			BettingMode: requestData.deepLinkSiteInfo.bettingMode !== -1 ? requestData.deepLinkSiteInfo.bettingMode.toString() : '',
			BetslipStyle: requestData.deepLinkSiteInfo.betslipStyle !== -1 ? requestData.deepLinkSiteInfo.betslipStyle.toString() : '',
			...((({ oddsType, skinMode, depositUrl, bettingMode, betslipStyle, ...rest }) => rest)(requestData.deepLinkSiteInfo))
		})
	}
	const options = {
		body: JSON.stringify(parseRequestData),
	};
	return options;
};

export const fetchUseTokenLogin = async (requestData: RequestJwtTokenData): Promise<GetJwtToken> => {
	if (!requestData.doUseRefreshToken) {
		return {
			Success: true,
			Code: null,
			Message: null,
			Data: {
				Token: '',
				UserName: '',
				SiteName: '',
				Url: '',
			},
		};
	}
	const options = getUseTokenLoginParameter(requestData);
	const response = await fetchComposer.postWithBearer(getTokenLoginUrl(requestData).href, options)(requestData.accessToken === 'unuse' ? undefined : requestData.accessToken);
	if (!response.ok) {
		throw new Error(`Login API failed with status ${response.status}`);
	}
	return await response.json();
};

/* use league id or match id to get target market */
export const fetchGetMarketByLeagueOrMatch = async (requestData: RequestDeeplinkSportData): Promise<string> => {
	if (requestData.leagueId === 0 && requestData.matchId === 0) {
		return '';
	}

	const methodName = requestData.matchId === 0 ? 'GetMarketByLeagueId' : 'GetMarketByMatchId';
	const apiUrl = new URL(`api/Market/${methodName}`, requestData.apiDomain);

	if (requestData.matchId === 0) {
		apiUrl.searchParams.set('leagueId', requestData.leagueId.toString());
		apiUrl.searchParams.set('targetMarketType', requestData.market || '');
	} else {
		apiUrl.searchParams.set('matchId', requestData.matchId.toString());
	}

	const response = await fetchComposer.getWithBearer(apiUrl.href)(requestData.authToken);

	if (!response.ok) {
		throw new Error(`API request failed with status ${response.status}`);
	}

	const result: GetMarketType = await response.json();
	return result.Data ?? '';
};

export const getTokenTypeEnum = (type: string | null) => {
	switch (type) {
		case '0':
			return TokenTypeEnum.Neuron;
		case '1':
			return TokenTypeEnum.SwitchModeWithId;
		case '2':
			return TokenTypeEnum.SwitchModeWithJwt;
		case '3':
			return TokenTypeEnum.None;
		default:
			return TokenTypeEnum.Default;
	}
}

export const getGalaxyDeeplinkParams = ({ 
	originalSearchParams,
	webSkinTypeAllow,
	locale,
}: {
	originalSearchParams: URLSearchParams,
	webSkinTypeAllow?: string,
	locale: string;
}) => {
	const searchParams = convertNextSearchParamsToLowerCase(originalSearchParams); // 統一使用小寫取用欄位

	const getSearchParamValue = (queryName: string) => searchParams.get(queryName);

	const queryNullishExtractor =
		Extractor(getSearchParamValue)
			.map((str: string) => str || null)
			.map(Nullish);

	const searchQuery = (word: string) => queryNullishExtractor.run(word);

	const token = searchParams.get('token') ?? '';
	const tokenType = getTokenTypeEnum(searchParams.get('tokentype'));

	const getSwitchFromEnum = (type: string | null) => {
		switch (type) {
			case '0':
				return SwitchFromEnum.Mobile;
			case '1':
				return SwitchFromEnum.Desktop;
			default:
				return SwitchFromEnum.Default;
		}
	}
	const switchFrom = getSwitchFromEnum(searchParams.get('switchfrom'));

	/* Handle URLSearchParams of deeplink URL */
	const deepLinkLanguage = searchParams.get('lang') || locale;

	const language = convertClientLocale(deepLinkLanguage);

	// Get compound params in mobile: rule => sport, menu type, market
	// Example => "types=1,0,e"
	const compoundValues = searchParams.get('types');

	const [compoundSport, compoundMenuType, compoundMarket]: MobileDeeplinkCompound =
		Maybe(compoundValues)
			.map(split(','))
			.fold(
				() => [],
				(types: string[]) => types
			);

	// If types and sport, market all exist in the URL, sport, market are main choices
	const querySport = ['act', 'sportid'].foldMap(searchQuery).x as string | null;
	const sport = Number(querySport ?? compoundSport) || 1;
	const menuType = Number(searchParams.get('menutype') ?? compoundMenuType) || 0;
	const market = (((['market', 'marketid'].foldMap(searchQuery).x as string | null) ?? compoundMarket) || 'l').toLowerCase();

	// Does not support multiple leagues
	const convertLeague = (leaguekey: string | null) => {
		const leagues = Maybe(leaguekey)
			.map(split(','))
			.fold(
				() => [],
				(leagues: number[]) => leagues
			);
		return leagues[0] | 0;
	};
	const league = convertLeague((['leagueid', 'leaguekey'].foldMap(searchQuery).x as string | null)) || 0;
	const match = Number(searchParams.get('matchid') ?? '0') || 0;

	// "act=streamerschedule" behaves the same as "types=streamerschedule"
	const types = (querySport ?? '').toLowerCase() === 'streamerschedule'
		? 'streamerschedule'
		: (searchParams.get('types') ?? '').toLowerCase();

	/**
	 * parse mobile lite to mars if allowed skins has 8
	 */
	const deepLinkSkin: number = stringEmptyCheck(['webskintype', 'skinmode', 'skin'].foldMap(searchQuery).x as string)
		.fold(
			() => -1,
			(skin: string) => Number(skin)
		);
	const skinMode: number = matchSome(deepLinkSkin)
		.on((skin: number) => switchFrom === SwitchFromEnum.Mobile && skin === 3 && webSkinTypeAllow?.split(',').includes('8'), () => 8)
		.otherwise((skin: number) => skin);
		
	const deepLinkSiteInfo: GalaxyDeepLinkSiteInfo = {
		homeUrl: (['homeurl', 'apihomeurl'].foldMap(searchQuery).x as string | null) ?? '',
		depositUrl: (['deposit', 'apidepositurl'].foldMap(searchQuery).x as string | null) ?? '',
		extendSessionUrl: (['extendsessionurl', 'apiextendsessionurl'].foldMap(searchQuery).x as string | null) ?? '',
		loginUrl: searchParams.get('loginurl') ?? '',
		signUpUrl: (['signupurl', 'singupurl'].foldMap(searchQuery).x as string | null) ?? '',
		game: (['game', 'types'].foldMap(searchQuery).x as string | null) ?? '',
		gameId: (['gameid', 'bettypeid'].foldMap(searchQuery).x as string | null) ?? '',
		oddsType: searchParams.get('otype') ? Number(searchParams.get('otype')) : -1, //TODO: odds page need use
		skinColor: searchParams.get('skincolor') ?? '',
		skinMode: skinMode, //TODO: use galaxy deeplink to change skin, phase 1 don't do this
		forceDarkMode: searchParams.get('forcedarkmode')? searchParams.get('forcedarkmode')?.toLowerCase() === 'true' : null,
		tsId: searchParams.get('tsid') ?? '',
		switchFrom: switchFrom,
		isApp: (searchParams.get('isapp') ?? 'false').toLowerCase() === 'true',
		themePreview: searchParams.get('themepreview') ?? '',
		templateId: searchParams.get('templateid') ?? '',
		dptoken: searchParams.get('dptoken') ?? '',
		switchFromUrl: searchParams.get('switchfromurl') ?? '',
		lgId: searchParams.get('lgid') ?? '',
		types,
		childType: (searchParams.get('childtype') ?? '').toLowerCase(),
		childTab: (searchParams.get('childtab') ?? '').toLowerCase(),
		clearCount: Number(searchParams.get('clearcount') ?? '0') || 0,
		targetMenuTab: Number(searchParams.get('targetmenutab') ?? '0') || 0,
		goToType: (searchParams.get('gototype') ??  '').toLowerCase(),
		// 新增投注偏好相關欄位
		bettingMode: searchParams.has('bettingmode') ? Number(searchParams.get('bettingmode')) : -1,
		betslipStyle: searchParams.has('betslipstyle') ? Number(searchParams.get('betslipstyle')) : -1,
	};

	return {
		language,
		querySport,
		sport,
		menuType,
		market,
		league,
		match,
		deepLinkSiteInfo,
		token,
		tokenType,
		doGetMarket: match !== 0 || league !== 0,
		doUseRefreshToken: (searchParams.get('lang') ?? '') != '' || !!token || deepLinkSiteInfo.homeUrl != '' || deepLinkSiteInfo.depositUrl != '' || deepLinkSiteInfo.extendSessionUrl != '' || deepLinkSiteInfo.loginUrl != '' 
		|| deepLinkSiteInfo.signUpUrl != '' || deepLinkSiteInfo.oddsType != -1 || deepLinkSiteInfo.skinColor != '' || deepLinkSiteInfo.skinMode != -1
		|| (searchParams.get('forcedarkmode') ?? '') != '' || deepLinkSiteInfo.tsId != '' || deepLinkSiteInfo.switchFrom != -1 || (searchParams.get('isapp') ?? '') != ''
		|| deepLinkSiteInfo.dptoken != '' || deepLinkSiteInfo.switchFromUrl != '' || deepLinkSiteInfo.lgId != '' || deepLinkSiteInfo.types != '' 
		|| deepLinkSiteInfo.bettingMode !== -1 || deepLinkSiteInfo.betslipStyle !== -1
	};
}

export const useGalaxyDeeplinkParams = () => {
	const { SiteConfigsData: siteConfigData } = useGetSiteConfigs();
	const originalSearchParams = useSearchParams();
	const locale = useLocale();
	return useMemo(() => getGalaxyDeeplinkParams({ originalSearchParams, webSkinTypeAllow: siteConfigData?.WebSkinTypeAllow, locale, }), [locale, originalSearchParams, siteConfigData?.WebSkinTypeAllow])
};
